import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  FileText,
  PlayCircle,
  ClipboardList,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Settings,
  Users,
  Lock,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageTitle } from "@/lib/title";
import { LessonContent } from "@/lib/markdown";
import { useToast } from "@/components/ui/Toast";
import type { Course, Module, LessonProgress, Lesson } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, statusTone } from "@/components/ui/Badge";
import { FullPageSpinner } from "@/components/ui/Spinner";

const lessonIcons: Record<Lesson["type"], typeof FileText> = {
  video: PlayCircle,
  document: FileText,
  text: FileText,
  quiz: HelpCircle,
  assignment: ClipboardList,
};

function LessonRow({
  lesson,
  enrolled,
  onCompleted,
}: {
  lesson: Lesson;
  enrolled: boolean;
  onCompleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { notify } = useToast();
  const Icon = lessonIcons[lesson.type];
  const completed = lesson.completed ?? false;
  const locked = enrolled && lesson.unlocked === false;

  const { mutate: markComplete, isPending } = useMutation({
    mutationFn: () => api.post(`/api/v1.0/lessons/${lesson.id}/complete`),
    onSuccess: () => {
      notify("Lesson marked complete");
      onCompleted();
    },
  });

  if (locked) {
    const typeLabel = lesson.type === "quiz" ? "Quiz" : lesson.type === "assignment" ? "Assignment" : `${lesson.duration_minutes} min`;
    return (
      <div
        className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2.5 text-slate-400"
        title="Complete the previous lesson to unlock this one"
      >
        <Lock className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-sm">{lesson.title}</span>
        <span className="text-xs">{typeLabel}</span>
      </div>
    );
  }

  if (lesson.type === "quiz" && lesson.quiz_id) {
    return (
      <Link
        to={`/quizzes/${lesson.quiz_id}/take`}
        className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-50"
      >
        {completed ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> : <Icon className="h-4 w-4 shrink-0 text-slate-400" />}
        <span className="flex-1 text-sm text-slate-800">{lesson.title}</span>
        <span className="text-xs text-slate-400">Quiz</span>
      </Link>
    );
  }

  if (lesson.type === "assignment" && lesson.assignment_id) {
    return (
      <Link
        to={`/assignments/${lesson.assignment_id}`}
        className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-50"
      >
        {completed ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> : <Icon className="h-4 w-4 shrink-0 text-slate-400" />}
        <span className="flex-1 text-sm text-slate-800">{lesson.title}</span>
        <span className="text-xs text-slate-400">Assignment</span>
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-slate-50"
      >
        {completed ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> : <Icon className="h-4 w-4 shrink-0 text-slate-400" />}
        <span className="flex-1 text-sm text-slate-800">{lesson.title}</span>
        <span className="text-xs text-slate-400">{lesson.duration_minutes} min</span>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="ml-7 mb-2 rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
          {lesson.video_url && (
            <div className="mb-3 aspect-video overflow-hidden rounded-lg bg-black">
              <video src={lesson.video_url} controls className="h-full w-full" />
            </div>
          )}
          {lesson.content && <LessonContent text={lesson.content} />}
          {lesson.attachment_url && (
            <a href={lesson.attachment_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-brand-600 hover:underline">
              View attachment
            </a>
          )}
          {enrolled && !completed && (
            <Button size="sm" className="mt-3" loading={isPending} onClick={() => markComplete()}>
              Mark as complete
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function CourseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isStaff = user?.role === "admin" || user?.role === "instructor";

  const { data: course, isLoading: loadingCourse } = useQuery({
    queryKey: ["courses", id],
    queryFn: () => api.get<Course>(`/api/v1.0/courses/${id}`),
    enabled: !!id,
  });

  const { data: modules, isLoading: loadingModules } = useQuery({
    queryKey: ["courses", id, "modules"],
    queryFn: () => api.get<Module[]>(`/api/v1.0/courses/${id}/modules`),
    enabled: !!id,
  });

  const { data: progress } = useQuery({
    queryKey: ["courses", id, "progress"],
    queryFn: () => api.get<LessonProgress[]>(`/api/v1.0/courses/${id}/progress`),
    enabled: !!id && user?.role === "student",
    retry: false,
  });

  const enrolled = !!progress;

  const { mutate: enroll, isPending: enrolling } = useMutation({
    mutationFn: () => api.post(`/api/v1.0/courses/${id}/enroll`),
    onSuccess: () => {
      notify("Enrolled! Happy learning.");
      void queryClient.invalidateQueries({ queryKey: ["courses", id, "progress"] });
      void queryClient.invalidateQueries({ queryKey: ["enrollments", "me"] });
    },
    onError: (err) => notify(err instanceof ApiError ? err.message : "Failed to enroll", "error"),
  });

  usePageTitle(course?.title);

  if (loadingCourse || loadingModules) return <FullPageSpinner />;
  if (!course) return <p>Course not found.</p>;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Badge tone={statusTone(course.status)}>{course.status}</Badge>
          </div>
          {course.description && <p className="max-w-2xl text-sm text-slate-500">{course.description}</p>}
        </div>
        <div className="flex shrink-0 gap-2">
          {isStaff && (
            <>
              <Button variant="secondary" onClick={() => navigate(`/courses/${id}/edit`)}>
                <Settings className="h-4 w-4" /> Manage
              </Button>
            </>
          )}
          {user?.role === "student" && !enrolled && course.status === "published" && (
            <Button loading={enrolling} onClick={() => enroll()}>
              Enroll now
            </Button>
          )}
        </div>
      </div>

      {isStaff && (
        <Card>
          <CardBody className="flex items-center gap-6 text-sm text-slate-600">
            <Link to={`/courses/${id}/edit`} className="flex items-center gap-1.5 text-brand-600 hover:underline">
              <Settings className="h-4 w-4" /> Course builder
            </Link>
            <span className="flex items-center gap-1.5">
              <Users className="h-4 w-4" /> Passing score: {course.passing_score}%
            </span>
          </CardBody>
        </Card>
      )}

      <div className="flex flex-col gap-4">
        {modules?.length ? (
          modules.map((module) => (
            <Card key={module.id}>
              <div className="border-b border-slate-100 px-5 py-3">
                <h3 className="font-medium text-slate-900">{module.title}</h3>
              </div>
              <CardBody className="flex flex-col gap-0.5">
                {module.lessons.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-slate-400">No lessons yet.</p>
                ) : (
                  module.lessons.map((lesson) => (
                    <LessonRow
                      key={lesson.id}
                      lesson={lesson}
                      enrolled={enrolled}
                      onCompleted={() => {
                        void queryClient.invalidateQueries({ queryKey: ["courses", id, "modules"] });
                        void queryClient.invalidateQueries({ queryKey: ["courses", id, "progress"] });
                        void queryClient.invalidateQueries({ queryKey: ["enrollments", "me"] });
                      }}
                    />
                  ))
                )}
              </CardBody>
            </Card>
          ))
        ) : (
          <p className="text-sm text-slate-500">This course has no content yet.</p>
        )}
      </div>
    </div>
  );
}
